import { PropsWithChildren } from 'react';

import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, Dialog, H2, Icon, Text } from '@betterment/samba-react';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';

import { trackClick } from '../../features/track';
import { FinancialServiceOption, ThemedAsset } from '../../graphql/schema';
import { urlBuilder } from '../../Shared/urlBuilder';

const CheckListItem = ({ item }: { item: string }) => {
  return (
    <Box css={{ display: 'flex', alignItems: 'start', gap: 'space.100' }}>
      <Icon name="Checkmark" size="small" />
      <Text>{item}</Text>
    </Box>
  );
};

type ModalProps = {
  title: string;
  ctaText: string;
  image: ThemedAsset;
  goToNextStep: () => void;
  analyticName: string;
};

const LearnMoreModal: React.FC<PropsWithChildren<ModalProps>> = ({
  goToNextStep,
  ctaText,
  title,
  image,
  analyticName,
  children,
}) => {
  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button variant="textPrimary" css={{ display: 'inline' }}>
          Learn more
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ display: 'grid' }}>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '173px',
            backgroundImage:
              'linear-gradient(270deg, token(colors.background.primary) 0%, token(colors.background.abyss) 100%)',
          }}
        >
          <ThemedImage themedAsset={image} alt={title} width="120" />
        </Box>
        <Box css={{ display: 'grid', p: 'space.400' }}>
          <Dialog.Title asChild={true}>
            <H2 variant="title300">{title}</H2>
          </Dialog.Title>
          <Dialog.Description asChild={true}>
            <Box css={{ display: 'grid', gap: 'space.200', paddingTop: 'space.150' }}>{children}</Box>
          </Dialog.Description>
          <Box css={{ display: 'flex' }}>
            <Button
              onClick={() => {
                trackClick({ name: analyticName, flow: 'BAN' });
                goToNextStep();
              }}
              css={{ marginTop: 'space.400' }}
            >
              {ctaText}
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

type Props = {
  financialServiceOption: FinancialServiceOption;
  isBAN: boolean;
  learnMoreModal?: React.ReactNode;
  goToNextStep: () => void;
};

const BAN_LEARN_MORE_DETAILS = [
  'Full-service wealth management and financial planning',
  'Advanced tax, estate, and trust strategies',
  'Customized investing portfolios managed by your advisor on Betterment',
  'Advice on equity compensation, life insurance, and lending',
];

const PREMIUM_LEARN_MORE_DETAILS = [
  '30-45 minute calls with our team of financial advisors',
  'Support setting up and optimizing your account',
  'Advice on saving, investing, and a tailored financial plan',
  'Exclusive benefits like higher cash rates, expanded investment options, and more',
];

export const FinancialServiceOptionCard: React.FC<Props> = ({ financialServiceOption, isBAN, goToNextStep }) => {
  const navigate = useAutoNavigate();
  const handlePremiumUpgrade = async () => {
    trackClick({ name: 'StartPremiumUpgrade', flow: 'BAN' });
    await navigate(urlBuilder.appAdviceInitiationUpgradeToPremium.absolute());
  };

  const handleBanMatch = () => {
    goToNextStep();
  };

  const getLearnMoreModal = () => {
    return isBAN ? (
      <LearnMoreModal
        title="What you get with a dedicated advisor"
        ctaText="Get matched with an advisor"
        image={financialServiceOption.image}
        goToNextStep={handleBanMatch}
        analyticName="GetMatched"
      >
        {BAN_LEARN_MORE_DETAILS.map(detail => (
          <CheckListItem key={detail} item={detail} />
        ))}
      </LearnMoreModal>
    ) : (
      <LearnMoreModal
        title="What you get with Betterment Premium"
        ctaText="Upgrade to Premium"
        image={financialServiceOption.image}
        goToNextStep={handlePremiumUpgrade}
        analyticName="PremiumConsult"
      >
        {PREMIUM_LEARN_MORE_DETAILS.map(detail => (
          <CheckListItem key={detail} item={detail} />
        ))}
      </LearnMoreModal>
    );
  };

  return (
    <Box
      css={{
        display: 'grid',
        border: '1px solid',
        borderColor: 'border',
        padding: 'space.300',
        borderRadius: 'medium',
      }}
    >
      <Box css={{ alignSelf: 'start', display: 'grid' }}>
        <ThemedImage themedAsset={financialServiceOption.image} alt={financialServiceOption.title} width="80" />
        <H2 variant="title300" css={{ paddingTop: 'space.300', paddingBottom: 'space.100' }}>
          {financialServiceOption.title}
        </H2>
        <Text>
          {financialServiceOption.description} {getLearnMoreModal()}
        </Text>
      </Box>
      <Box css={{ alignSelf: 'end', display: 'grid' }}>
        <Text variant="text300" css={{ paddingTop: 'space.200' }}>
          {financialServiceOption.serviceFee}
        </Text>
        <Text variant="text50" color="text.weak">
          {financialServiceOption.feeDisclosure}
        </Text>
        {isBAN ? (
          <Button onClick={handleBanMatch} css={{ marginTop: 'space.400', marginBottom: 'space.200' }}>
            Get matched with an advisor
          </Button>
        ) : (
          <Button onClick={handlePremiumUpgrade} css={{ marginTop: 'space.400', marginBottom: 'space.200' }}>
            Upgrade to Premium
          </Button>
        )}
        <Text variant="text50" color="text.weak" css={{ textAlign: 'center' }}>
          {financialServiceOption.minimumBalanceDisclosure}
        </Text>
      </Box>
    </Box>
  );
};
